import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../../config";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { Table, Input, Space, Button } from "antd";
import { SearchOutlined } from '@ant-design/icons';
import SelectUsersExcelButton from "./SelectUsersExcelButton";
import VersionConflictModal from "../Commons/VersionConflictModal";
import {
    extractVersion,
    getVersionConflictMessage,
    isVersionConflict,
} from "../../utils/versionConflict";

const EditCourse = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const courseId = location.state?.course.courseId;
    const [isLoading, setIsLoading] = useState(false);
    const [isOutdated, setIsOutdated] = useState(false);
    const [versionConflict, setVersionConflict] = useState({
        open: false,
        message: "",
    });
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [selectedImage, setSelectedImage] = useState(null);

    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [allVideos, setAllVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [videos, setVideos] = useState([]);
    const [userLoading, setUserLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const [userSearchTerm, setUserSearchTerm] = useState("");
    const [videoSearchTerm, setVideoSearchTerm] = useState("");
    const [tagInput, setTagInput] = useState("");

    const [userPagination, setUserPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [videoPagination, setVideoPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [searchFilters, setSearchFilters] = useState({ name: "", title: "" });
    const [UserSearchFilters, setUserSearchFilters] = useState({
        username: "",
        personalId: "",
        lastname: "",
        firstname: "",
        phoneNumber: ""
    });

    const [formData, setFormData] = useState({
        title: "",
        shortDescription: "",
        description: "",
        price: 0,
        tags: [],
        enrolledUserIds: [],
        courseVideos: [],
        pictureUrl: "",
        isPublished: false,
        teacher: "",
        version: 0,
    });


    const handleSearchChange = (e, columnName) => {


        let value = e.target.value;

        const newFilters = {
            ...searchFilters,
            [columnName]: value
        };


        setSearchFilters(newFilters);



        const filteredData = videos.filter(video => {
            return (
                (!newFilters.name || video.name.toLowerCase().includes(newFilters.name.toLowerCase())) &&
                (!newFilters.title || video.title.toLowerCase().includes(newFilters.title.toLowerCase()))
            );
        });

        setAllVideos(filteredData);

    };
    const dedupeUsersById = (arr) => {
        const m = new Map();
        arr.forEach(u => m.set(u.userID, u));
        return Array.from(m.values());
    };
    const putSelectedUsersOnTop = (selectedIds, usersList) => {
        const sel = new Set(selectedIds || []);
        const selected = [];
        const remaining = [];
        usersList.forEach(u => (sel.has(u.userID) ? selected : remaining).push(u));
        return [...selected, ...remaining];
    };


    const buildUserFilter = (filters) => {
        if (filters.username) return `username=*${filters.username}/i`;
        if (filters.personalId) return `personalId=*${filters.personalId}/i`;
        if (filters.lastname) return `lastname=*${filters.lastname}/i`;
        if (filters.firstname) return `firstname=*${filters.firstname}/i`;
        if (filters.phoneNumber) return `phoneNumber=*${filters.phoneNumber}/i`;
        return undefined;
    };

    const fetchUsersWithFilters = async (page = 1, pageSize = 5, filters = UserSearchFilters) => {
        try {
            setUserLoading(true);
            const { data } = await axios.get(`${config.apiBaseUrl}/api/Users/GetUsersList`, {
                params: {
                    page,
                    pageSize,
                    filter: buildUserFilter(filters),
                },
                headers: { HeaderKey: config.HeaderKey },
                withCredentials: true,
            });

            const incomingUsers = data.users || [];

            setUsers(prevUsers => dedupeUsersById([...prevUsers, ...incomingUsers]));
            setAllUsers(incomingUsers);
            setUserPagination(prev => ({ ...prev, current: page, pageSize, total: data.totalCount }));
        } catch (error) {
            toast.error(<p dir="rtl" className="font-custom">جستجو با خطا مواجه شد</p>);
            if (error.response?.status === 401) navigate("/");
        } finally {
            setUserLoading(false);
        }
    };

    const userSearch = async () => {
        await fetchUsersWithFilters(1, userPagination.pageSize, UserSearchFilters);
    };

    const handleUserFilterClear = (column) => {
        const next = { ...UserSearchFilters, [column]: "" };
        setUserSearchFilters(next);
        fetchUsersWithFilters(1, userPagination.pageSize, next);
    };




    const orderUsersAndVideos = async (selectedUserIds, selectedCourseVideos, usersList, videosList) => {
        const selectedVideoIds = selectedCourseVideos.map(v => v.videoId);
        const selectedFirstVideos = videosList
            .filter(v => selectedVideoIds.includes(v.id))
            .sort((a, b) => {
                const aOrder = selectedCourseVideos.find(cv => cv.videoId === a.id)?.order || 0;
                const bOrder = selectedCourseVideos.find(cv => cv.videoId === b.id)?.order || 0;
                return aOrder - bOrder;
            });
        const remainingVideos = videosList.filter(v => !selectedVideoIds.includes(v.id));
        const finalVideos = [...selectedFirstVideos, ...remainingVideos];
        setAllVideos(finalVideos);

        if (!selectedUserIds?.length) {
            setUsers(usersList);
            setAllUsers(usersList);
            return;
        }

        const filter = selectedUserIds.map(id => `(userID=${id})`).join("|");

        const { data } = await axios.get(`${config.apiBaseUrl}/api/Users/GetUsersList`, {
            params: { page: 1, pageSize: 5, filter },
            headers: { HeaderKey: config.HeaderKey },
            withCredentials: true,
        });

        const selected = data.users || [];
        const remaining = (usersList || []).filter(u => !selectedUserIds.includes(u.userID));
        const finalUsers = [...selected, ...remaining];

        setUsers(finalUsers);
        setAllUsers(finalUsers);
    };

    const loadCourseData = useCallback(async () => {
        if (!courseId) return;
        setLoading(true);
        try {
            const [courseDetails, usersResponse, videosResponse] = await Promise.all([
                axios.get(`${config.apiBaseUrl}/api/Course/getCourse?id=${courseId}`, {
                    headers: { HeaderKey: config.HeaderKey },
                    withCredentials: true,
                }),
                axios.get(`${config.apiBaseUrl}/api/users/getuserslist`, {
                    params: { page: 1, pageSize: 5 },
                    headers: { HeaderKey: config.HeaderKey },
                    withCredentials: true,
                }),
                axios.get(`${config.apiBaseUrl}/api/video/GetFilteredList`, {
                    params: {
                        page: 1,
                        pageSize: 51000,
                        orderBy: "uploadedAt desc",
                        filter: "completed=true",
                    },
                    headers: { HeaderKey: config.HeaderKey },
                    withCredentials: true,
                }),
            ]);

            const course = courseDetails.data;
            setFormData({
                title: course.title || "",
                shortDescription: course.shortDescription || "",
                description: course.description || "",
                price: course.price || 0,
                tags: course.tags || [],
                enrolledUserIds: course.enrolledUserIds || [],
                courseVideos: course.courseVideos || [],
                pictureUrl: course.pictureUrl || "",
                isPublished: course.isPublished || false,
                teacher: course.teacher || "",
                version: extractVersion(course) ?? 0,
            });
            setSelectedVideos(course.courseVideos?.map(v => v.videoId) || []);
            setSelectedImage(null);

            const usersDeduped = dedupeUsersById(usersResponse.data.users);
            setUsers(usersDeduped);
            setAllUsers(putSelectedUsersOnTop(course.enrolledUserIds, usersDeduped));
            setSelectedUsers(course.enrolledUserIds || []);

            setFormData(prev => ({ ...prev, enrolledUserIds: course.enrolledUserIds || [] }));

            setVideos(videosResponse.data.videos);
            setAllVideos(videosResponse.data.videos);

            orderUsersAndVideos(
                course.enrolledUserIds,
                course.courseVideos,
                usersResponse.data.users,
                videosResponse.data.videos
            );

            setUserPagination({
                current: 1,
                pageSize: 5,
                total: usersResponse.data.totalCount,
            });

            setVideoPagination({
                current: 1,
                pageSize: 5,
                total: videosResponse.data.totalCount,
            });

            setIsOutdated(false);
        } catch (error) {
            toast.error("خطا در دریافت اطلاعات اولیه", { autoClose: 3000 });
            if (error.response && error.response.status === 401) {
                navigate("/");
            }
            throw error;
        } finally {
            setLoading(false);
        }
    }, [courseId, navigate]);

    useEffect(() => {
        if (courseId) {
            loadCourseData().catch(() => {});
        } else {
            toast.error("هیچ شناسه دوره‌ای یافت نشد!");
            navigate("/dashboard/courses");
        }
    }, [courseId, navigate, loadCourseData]);
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedImage(e.target.files[0]);
        }
    };

    const handleUserSearchChange = (e, column) => {
        const value = e.target.value;
        setUserSearchFilters((prev) => ({ ...prev, [column]: value }));
    };


    const removeSelectedUser = (userId) => {
        setSelectedUsers((prev) => prev.filter((id) => id !== userId));
        setFormData((prev) => ({
            ...prev,
            enrolledUserIds: prev.enrolledUserIds.filter((id) => id !== userId),
        }));
    };
    const removeSelectedVideo = (videoId) => {
        setSelectedVideos((prev) => prev.filter((id) => id !== videoId));
        setFormData((prev) => {
            const kept = prev.courseVideos.filter((v) => v.videoId !== videoId);
            const normalized = kept
                .sort((a, b) => a.order - b.order)
                .map((v, i) => ({ ...v, order: i + 1 }));
            return { ...prev, courseVideos: normalized };
        });
    };
    const moveSelectedVideo = (videoId, dir) => {
        setFormData((prev) => {
            const sorted = [...prev.courseVideos].sort((a, b) => a.order - b.order);
            const idx = sorted.findIndex((v) => v.videoId === videoId);
            if (idx === -1) return prev;

            const swapIdx = idx + dir;
            if (swapIdx < 0 || swapIdx >= sorted.length) return prev;

            const tmpOrder = sorted[idx].order;
            sorted[idx].order = sorted[swapIdx].order;
            sorted[swapIdx].order = tmpOrder;

            const normalized = sorted
                .sort((a, b) => a.order - b.order)
                .map((v, i) => ({ ...v, order: i + 1 }));

            return { ...prev, courseVideos: normalized };
        });
    };

    const handleUserSelection = (newSelectedKeys) => {
        setSelectedUsers((prevSelected) => {
            const visibleIds = new Set(allUsers.map((user) => user.userID));
            const updated = new Set(
                prevSelected.filter((id) => !visibleIds.has(id) || newSelectedKeys.includes(id))
            );
            newSelectedKeys.forEach((key) => updated.add(key));

            const finalSelection = Array.from(updated);
            setFormData((prev) => ({ ...prev, enrolledUserIds: finalSelection }));
            return finalSelection;
        });
    };

    const handleVideoSelection = (newSelectedKeys) => {
        setSelectedVideos(newSelectedKeys);

        setFormData(prev => {
            let updatedCourseVideos = [...prev.courseVideos];

            newSelectedKeys.forEach(key => {
                const isAlreadyAdded = updatedCourseVideos.some(cv => cv.videoId === key);
                if (!isAlreadyAdded) {
                    updatedCourseVideos.push({ videoId: key, order: 0 }); 
                }
            });

            updatedCourseVideos = updatedCourseVideos.filter(cv => newSelectedKeys.includes(cv.videoId));

            updatedCourseVideos = updatedCourseVideos.map((video, index) => ({
                ...video,
                order: index + 1,
            }));
            return {
                ...prev,
                courseVideos: updatedCourseVideos,
            };
        });
    };

    const handleOrderChange = (videoId, newOrder) => {
        setFormData((prev) => ({
            ...prev,
            courseVideos: prev.courseVideos.map((v) =>
                v.videoId === videoId ? { ...v, order: newOrder } : v
            ),
        }));
    };

    const selectedVideosOrdered = formData.courseVideos
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(v => allVideos.find(video => video.id === v.videoId))
        .filter(Boolean);

    const filteredVideos = allVideos.filter(video => {
        const { name, title } = searchFilters;
        const matches = (
            (!name || video.name?.toLowerCase().includes(name.toLowerCase())) &&
            (!title || video.title?.toLowerCase().includes(title.toLowerCase()))
        );
        return matches && !formData.courseVideos.some(v => v.videoId === video.id);
    });
    const selectedUsersOrdered = formData.enrolledUserIds
        .map(id => users.find(u => u.userID === id) || allUsers.find(u => u.userID === id))
        .filter(Boolean);

    const filteredUsers = allUsers.filter(user => {
        const { username, personalId, firstname, lastname, phoneNumber } = UserSearchFilters;
        const matches = (
            (!username || user.username?.toLowerCase().includes(username.toLowerCase())) &&
            (!personalId || user.personalId?.toLowerCase().includes(personalId.toLowerCase())) &&
            (!firstname || user.firstname?.toLowerCase().includes(firstname.toLowerCase())) &&
            (!lastname || user.lastname?.toLowerCase().includes(lastname.toLowerCase())) &&
            (!phoneNumber || user.phoneNumber?.toLowerCase().includes(phoneNumber.toLowerCase()))
        );
        return matches && !formData.enrolledUserIds.includes(user.userID); 
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isOutdated) {
            setVersionConflict((prev) => ({ ...prev, open: true }));
            return;
        }
        setIsLoading(true);

        if (!courseId) {
            toast.error("شناسه دوره نامعتبر است!", { autoClose: 3000 });
            setIsLoading(false);
            return;
        }

        const formDataToSend = new FormData();

        formDataToSend.append("id", courseId); 
        formDataToSend.append("title", formData.title);
        formDataToSend.append("shortDescription", formData.shortDescription);
        formDataToSend.append("description", formData.description);
        formDataToSend.append("price", formData.price);
        formDataToSend.append("pictureUrl", formData.pictureUrl || "");
        formDataToSend.append("isPublished", formData.isPublished);
        formDataToSend.append("teacher", formData.teacher);
        formDataToSend.append("Version", formData.version ?? 0);

        formData.tags.forEach((tag) => {
            formDataToSend.append("tags", tag);
        });

        (formData.enrolledUserIds || []).forEach((id) => {
            formDataToSend.append("enrolledUserIds", id);
        });
        formData.courseVideos.forEach((videoObj, index) => {
            formDataToSend.append(`CourseVideos[${index}].VideoId`, videoObj.videoId);
            formDataToSend.append(`CourseVideos[${index}].Order`, videoObj.order);
        });

        if (selectedImage) {
            formDataToSend.append("image", selectedImage);
        }

        try {
            const response = await axios.post(
                `${config.apiBaseUrl}/api/Course/UpdateCourse`,
                formDataToSend,
                {
                    headers: {
                        HeaderKey:
                            config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            if (response.status === 200) {
                const nextVersion = extractVersion(response.data);
                if (nextVersion != null) {
                    setFormData((prev) => ({ ...prev, version: nextVersion }));
                }
                toast.success(<p dir="rtl" className="font-custom">دوره با موفقیت به‌روزرسانی شد</p>, { autoClose: 3000 });
                setTimeout(() => {
                    navigate("/dashboard/courses");
                }, 1000);
            } else {
                toast.error("خطا در به‌روزرسانی دوره!", { autoClose: 3000 });
            }
        } catch (error) {
            if (isVersionConflict(error)) {
                setIsOutdated(true);
                setVersionConflict({
                    open: true,
                    message: getVersionConflictMessage(error),
                });
            } else {
                toast.error("خطا در برقراری ارتباط با سرور!", { autoClose: 3000 });
            }
            if (error.response && error.response.status === 401) {
                navigate("/");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVersionConflictRefresh = async () => {
        setIsRefreshing(true);
        try {
            await loadCourseData();
            setVersionConflict({ open: false, message: "" });
            toast.success(<p dir="rtl" className="font-custom">اطلاعات دوره بازخوانی شد</p>, { autoClose: 2500 });
        } catch {
            // loadCourseData already toasts
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleVersionConflictCancel = () => {
        setVersionConflict({ open: false, message: "" });
        navigate("/dashboard/courses");
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const handleTagChange = (e) => {
        const value = e.target.value;
        const separators = [",", "،"]; 

        if (separators.some((sep) => value.includes(sep))) {
            const newTag = value.replace(/[,،]/g, "").trim();
            if (newTag && !formData.tags.includes(newTag)) {
                setFormData((prev) => ({
                    ...prev,
                    tags: [...prev.tags, newTag],
                }));
            }
            setTagInput("");
        } else {
            setTagInput(value);
        }
    };

    const removeTag = (index) => {
        setFormData((prev) => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index),
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>در حال بارگذاری...</p>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6 rtl font-custom">
            <div className="w-full max-w-4xl bg-white p-8 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center mb-6">
                    ویرایش دوره
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="text"
                            name="shortDescription"
                            placeholder="توضیح کوتاه"
                            value={formData.shortDescription}
                            onChange={handleChange}
                            className="p-3 border rounded-md text-right"
                            required
                            dir="rtl"
                        />
                        <input
                            type="text"
                            name="title"
                            placeholder="عنوان دوره"
                            value={formData.title}
                            onChange={handleChange}
                            className="p-3 border rounded-md text-right"
                            required
                            dir="rtl"
                        />
                    </div>

                    <textarea
                        name="description"
                        placeholder="توضیح کامل دوره"
                        value={formData.description}
                        onChange={handleChange}
                        rows={4}
                        className="w-full p-3 border rounded-md text-right"
                        required
                        dir="rtl"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                            type="number"
                            name="price"
                            placeholder="قیمت دوره (تومان)"
                            value={formData.price}
                            onChange={handleChange}
                            className="w-full p-3 border rounded-md"
                            required
                        />
                        <input
                            type="text"
                            name="teacher"
                            placeholder="مدرس دوره"
                            value={formData.teacher}
                            onChange={handleChange}
                            className="p-3 border rounded-md text-right"
                            required
                            dir="rtl"
                        />
                    </div>

                    <div>
                        <label className="block mb-1 font-semibold text-gray-700">
                            تصویر دوره:
                        </label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="p-2 border rounded-md"
                        />
                        {selectedImage ? (
                            <p className="mt-1 text-sm text-green-600">
                                {selectedImage.name} : تصویر انتخاب شده
                            </p>
                        ) : formData.pictureUrl ? (
                            <p className="mt-1 text-sm text-gray-600">
                                تصویر فعلی: {formData.pictureUrl}
                            </p>
                        ) : null}
                    </div>

                    <div>
                        <div className="p-3 border rounded-md w-full text-right">
                            <div className="flex flex-wrap gap-2 mb-2">
                                {formData.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full flex items-center"
                                    >
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => removeTag(index)}
                                            className="ml-2 text-red-500 font-bold hover:text-red-700"
                                        >
                                            ✖
                                        </button>
                                    </span>
                                ))}
                            </div>

                            <input
                                type="text"
                                name="tags"
                                placeholder="تگ‌ها را با کاما یا ویرگول جدا کنید"
                                value={tagInput}
                                onChange={handleTagChange}
                                className="p-2 border rounded-md w-full text-right focus:outline-none"
                                dir="rtl"
                            />
                        </div>
                    </div>
                    <div className="grid text-right rtl grid-cols-1  gap-4">
                        <div className="flex items-center my-8">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <h2 className="mx-4 text-xl font-bold text-gray-400 whitespace-nowrap font-custom">
                                انتخاب کاربران دوره
                            </h2>
                            <div className="flex-grow border-t border-gray-300"></div>
                        </div>
                        <div className="mb-3 flex justify-start">
                            <SelectUsersExcelButton
                                onSelected={(newUsers) => {
                                    setAllUsers(prev => {
                                        const map = new Map(prev.map(u => [u.userID, u]));
                                        newUsers.forEach(u => map.set(u.userID, u));
                                        return Array.from(map.values());
                                    });
                                    setUsers(prev => {
                                        const map = new Map(prev.map(u => [u.userID, u]));
                                        newUsers.forEach(u => map.set(u.userID, u));
                                        return Array.from(map.values());
                                    });
                                    setSelectedUsers(prev => {
                                        const map = new Map(prev.map(id => [id, id]));
                                        newUsers.forEach(u => map.set(u.userID, u.userID));
                                        const final = Array.from(map.values());
                                        setFormData(f => ({ ...f, enrolledUserIds: final }));
                                        return final;
                                    });
                                }}
                            />
                        </div>
                        <Table
                            rowSelection={{
                                selectedRowKeys: selectedUsers,
                                onChange: handleUserSelection,
                                preserveSelectedRowKeys: true,
                                fixed: 'left',
                            }}
                            columns={[
                                {
                                    title: 'نام خانوادگی',
                                    dataIndex: 'lastname',
                                    key: 'lastname',
                                    align: 'right',
                                    className: 'font-custom',
                                    filterDropdown: () => (
                                        <div style={{ padding: 8 }} className="font-custom">
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    placeholder="جستجوی نام خانوادگی"
                                                    allowClear
                                                    className="font-custom"
                                                    onClear={() => handleUserFilterClear("lastname")}
                                                    value={UserSearchFilters.lastname}
                                                    onChange={(e) => {
                                                        handleUserSearchChange(e, "lastname");
                                                    }}
                                                    onPressEnter={() => {
                                                        userSearch();
                                                    }}
                                                />
                                                <Button type="primary"
                                                    icon={<SearchOutlined />}
                                                    onClick={() => {
                                                        userSearch();
                                                    }}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                                {
                                    title: 'نام کاربری',
                                    dataIndex: 'username',
                                    key: 'username',
                                    align: 'right',
                                    className: 'font-custom'
                                    , filterDropdown: () => (
                                        <div style={{ padding: 8 }} className="font-custom">
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    placeholder="جستجوی نام کاربری"
                                                    allowClear
                                                    className="font-custom"
                                                    onClear={() => handleUserFilterClear("username")}
                                                    value={UserSearchFilters.username}
                                                    onChange={(e) => {
                                                        handleUserSearchChange(e, "username");
                                                    }}
                                                    onPressEnter={() => {
                                                        userSearch();
                                                    }}
                                                />
                                                <Button type="primary"
                                                    icon={<SearchOutlined />}
                                                    onClick={() => {
                                                        userSearch();
                                                    }}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                                {
                                    title: 'کد ملی',
                                    dataIndex: 'personalId',
                                    key: 'personalId',
                                    align: 'center',
                                    className: 'font-custom',
                                    filterDropdown: () => (
                                        <div style={{ padding: 8 }} className="font-custom">
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    placeholder="جستجوی کدملی"
                                                    allowClear
                                                    className="font-custom"
                                                    onClear={() => handleUserFilterClear("personalId")}
                                                    value={UserSearchFilters.personalId}
                                                    onChange={(e) => {
                                                        handleUserSearchChange(e, "personalId");
                                                    }}
                                                    onPressEnter={() => {
                                                        userSearch();
                                                    }}
                                                />
                                                <Button type="primary"
                                                    icon={<SearchOutlined />}
                                                    onClick={() => {
                                                        userSearch();
                                                    }}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                                {
                                    title: 'شماره تلفن',
                                    dataIndex: 'phoneNumber',
                                    key: 'phoneNumber',
                                    align: 'center',
                                    className: 'font-custom',
                                    filterDropdown: () => (
                                        <div style={{ padding: 8 }} className="font-custom">
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    placeholder="جستجوی شماره تلفن"
                                                    allowClear
                                                    className="font-custom"
                                                    onClear={() => handleUserFilterClear("phoneNumber")}
                                                    value={UserSearchFilters.phoneNumber}
                                                    onChange={(e) => {
                                                        handleUserSearchChange(e, "phoneNumber");
                                                    }}
                                                    onPressEnter={() => {
                                                        userSearch();
                                                    }}
                                                />
                                                <Button type="primary"
                                                    icon={<SearchOutlined />}
                                                    onClick={() => {
                                                        userSearch();
                                                    }}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                            ]}
                            dataSource={allUsers}
                            rowKey="userID"
                            pagination={{
                                current: userPagination.current,
                                pageSize: userPagination.pageSize,
                                total: userPagination.total,
                                onChange: (page, pageSize) => {
                                    fetchUsersWithFilters(page, pageSize, UserSearchFilters);
                                },
                            }}
                            loading={loading || userLoading}
                        />


                        <div className="flex items-center my-8">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <h2 className="mx-4 text-xl font-bold text-gray-600 whitespace-nowrap font-custom">
                                موارد انتخاب‌شده
                            </h2>
                            <div className="flex-grow border-t border-gray-300"></div>
                        </div>

                        {/* Selected Users Table */}
                        <h3 className="text-lg font-semibold mb-2 text-right font-custom">کاربران انتخاب‌شده</h3>
                        <Table
                            columns={[
                                { title: 'نام کاربری', dataIndex: 'username', key: 'username', align: 'right', className: 'font-custom' },
                                { title: 'نام', dataIndex: 'firstname', key: 'firstname', align: 'right', className: 'font-custom' },
                                { title: 'نام خانوادگی', dataIndex: 'lastname', key: 'lastname', align: 'right', className: 'font-custom' },
                                { title: 'شماره تلفن', dataIndex: 'phoneNumber', key: 'phoneNumber', align: 'center', className: 'font-custom' },
                                {
                                    title: 'حذف',
                                    key: 'actions',
                                    className:'font-custom',
                                    align: 'center',
                                    render: (_, record) => (
                                        <Button className="font-custom" danger size="small" onClick={() => removeSelectedUser(record.userID)}>
                                            حذف
                                        </Button>
                                    ),
                                },
                            ]}
                            dataSource={selectedUsersOrdered}
                            rowKey="userID"
                            pagination={false}
                        />
                        <div className="flex items-center my-8">
                            <div className="flex-grow border-t border-gray-300"></div>
                            <h2 className="mx-4 text-xl font-bold text-gray-400 whitespace-nowrap font-custom">
                                انتخاب ویدئوهای دوره
                            </h2>
                            <div className="flex-grow border-t border-gray-300"></div>
                        </div>
                        <Table
                            rowSelection={{
                                selectedRowKeys: selectedVideos,
                                onChange: handleVideoSelection,
                                fixed: 'left',
                                preserveSelectedRowKeys: true,
                            }}
                            columns={[
                                {
                                    title: 'نام فایل',
                                    dataIndex: 'name',
                                    key: 'name',
                                    align: 'right',
                                    filterDropdown: () => (
                                        <div style={{ padding: 8 }}>
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    className="font-custom"
                                                    placeholder="جستجوی نام فایل"
                                                    allowClear
                                                    value={searchFilters.name}
                                                    onChange={(e) => handleSearchChange(e, "name")}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                                {
                                    title: 'عنوان',
                                    dataIndex: 'title',
                                    key: 'title',
                                    align: 'right',
                                    filterDropdown: () => (
                                        <div style={{ padding: 8 }}>
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Input
                                                    className="font-custom"
                                                    placeholder="جستجوی عنوان ویدیو"
                                                    allowClear
                                                    value={searchFilters.title}
                                                    onChange={(e) => handleSearchChange(e, "title")}
                                                />
                                            </Space.Compact>
                                        </div>
                                    ),
                                },
                                {
                                    title: 'اولویت',
                                    key: 'order',
                                    render: (_, record) => {
                                        const current = formData.courseVideos.find(v => v.videoId === record.id);
                                        return current ? (
                                            <input
                                                type="number"
                                                value={current.order}
                                                onChange={(e) => handleOrderChange(record.id, parseInt(e.target.value))}
                                                className="w-16 p-1 border rounded-md text-center"
                                                min="1"
                                            />
                                        ) : null;
                                    },
                                },
                            ]}
                            dataSource={allVideos}
                            rowKey="id"
                            pagination={{
                                current: videoPagination.current,
                                pageSize: videoPagination.pageSize,
                                total: videoPagination.total,
                                onChange: (page, pageSize) => {
                                    setVideoPagination(prev => ({ ...prev, current: page, pageSize }));
                                },
                            }}
                            loading={loading}
                        />

                        {/* Selected Videos Table */}
                        <h3 className="text-lg font-semibold mb-2 text-right font-custom">ویدئوهای انتخاب‌شده</h3>
                        <Table
                            columns={[
                                {
                                    title: 'اولویت',
                                    key: 'order',
                                    width: 110,
                                    align: 'center',
                                    render: (_, record) => {
                                        const cv = formData.courseVideos.find((v) => v.videoId === record.id);
                                        return cv ? cv.order : '-';
                                    },
                                },
                                { title: 'نام فایل', dataIndex: 'name', key: 'name', align: 'right', className: 'font-custom' },
                                { title: 'عنوان', dataIndex: 'title', key: 'title', align: 'right', className: 'font-custom' },
                                {
                                    title: 'مرتب‌سازی',
                                    key: 'sort',
                                    className:'font-custom',
                                    align: 'center',
                                    render: (_, record) => (
                                        <Space>
                                            <Button className="font-custom" size="small" onClick={() => moveSelectedVideo(record.id, -1)}>بالا</Button>
                                            <Button className="font-custom" size="small" onClick={() => moveSelectedVideo(record.id, +1)}>پایین</Button>
                                        </Space>
                                    ),
                                },
                                {
                                    title: 'حذف',
                                    key: 'actions',
                                    align: 'center',
                                    className:'font-custom',
                                    render: (_, record) => (
                                        <Button className="font-custom" danger size="small" onClick={() => removeSelectedVideo(record.id)}>
                                            حذف
                                        </Button>
                                    ),
                                },
                            ]}
                            dataSource={selectedVideosOrdered}
                            rowKey="id"
                            pagination={false}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading || isOutdated}
                        className="w-full flex justify-center items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm px-4 py-2.5 transition-colors duration-300 disabled:opacity-70"
                    >
                        {isLoading ? <FaSpinner className="animate-spin" /> : "ویرایش دوره"}
                    </button>
                </form>
            </div>

            <VersionConflictModal
                open={versionConflict.open}
                message={versionConflict.message}
                refreshing={isRefreshing}
                onRefresh={handleVersionConflictRefresh}
                onCancel={handleVersionConflictCancel}
            />

            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                theme="dark"
                transition={Bounce}
                className="rtl font-custom"
            />
        </div>
    );
};

export default EditCourse;
